/**
 * One small dialog for the Home scene's folder questions.
 *
 * Two shapes, one component — the web's `PromptDialog` makes the same
 * economy: a **name** ("New folder", "Rename…") and a **choice** among the
 * workspace's folders ("Move to folder…"). Fluent `Dialog`, like every other
 * modal in the pane. Resolves through `onSubmit`; every other exit is
 * `onCancel`, so the scene never waits on a dialog that quietly closed.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import { type FC, useEffect, useState } from "react";
import { fonts, inkColors } from "../tokens";

export type HomePromptRequest =
  | { kind: "name"; title: string; label: string; initial?: string; confirm?: string }
  | { kind: "choice"; title: string; label: string; options: Array<{ value: string; label: string }> };

const useStyles = makeStyles({
  surface: { maxWidth: "360px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "8px" },
  label: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink3 },
  list: { display: "flex", flexDirection: "column", gap: "2px" },
  option: {
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink,
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: "6px",
    padding: "7px 9px",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#F5F1FA" },
  },
});

export const HomePromptDialog: FC<{
  request: HomePromptRequest | null;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}> = ({ request, onSubmit, onCancel }) => {
  const styles = useStyles();
  const [value, setValue] = useState("");
  useEffect(() => {
    setValue(request?.kind === "name" ? (request.initial ?? "") : "");
  }, [request]);
  const open = request !== null;
  const trimmed = value.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{request?.title ?? ""}</DialogTitle>
          <DialogContent>
            {request?.kind === "name" && (
              <form
                id="home-prompt-form"
                className={styles.body}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (trimmed.length > 0) onSubmit(trimmed);
                }}
              >
                <span className={styles.label}>{request.label}</span>
                <Input
                  value={value}
                  maxLength={120}
                  aria-label={request.label}
                  onChange={(_, data) => setValue(data.value)}
                  size="small"
                />
              </form>
            )}
            {request?.kind === "choice" && (
              <div className={styles.body}>
                <span className={styles.label}>{request.label}</span>
                <div className={styles.list}>
                  {request.options.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      className={styles.option}
                      onClick={() => onSubmit(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Cancel
            </Button>
            {request?.kind === "name" && (
              <Button appearance="primary" type="submit" form="home-prompt-form" disabled={trimmed.length === 0}>
                {request.confirm ?? "Save"}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default HomePromptDialog;
